import { create } from 'zustand';
import { log } from '@/lib/logger';

interface SelectionStore {
  selected: Set<string>;
  /** When true, MapView keeps the (single) selected drone's marker on screen. */
  followSelected: boolean;
  /** What the operator most recently interacted with. Drives the right-pane
   *  swap so picking an asset shows telemetry even mid-waypoint-editing. */
  focus: 'asset' | 'waypoint';
  select: (id: string, opts?: { multi?: boolean }) => void;
  /** Replace the selection with exactly these ids (group header select-all).
   *  No toggle semantics — always ends selected. */
  selectMany: (ids: string[]) => void;
  deselect: (id: string) => void;
  clear: () => void;
  toggle: (id: string) => void;
  isSelected: (id: string) => boolean;
  setFollow: (on: boolean) => void;
  setFocus: (focus: 'asset' | 'waypoint') => void;
  /** Drop any selected ids that aren't in the given roster. Called by the WS
   *  dispatcher after fresh `robots` snapshots / robot_removed events so a
   *  reconnect doesn't leave dangling selection of vanished drones. */
  pruneTo: (validIds: Iterable<string>) => void;
}

export const useSelection = create<SelectionStore>((set, get) => ({
  selected: new Set(),
  followSelected: false,
  focus: 'asset',
  setFollow: (on) => set({ followSelected: on }),
  setFocus: (focus) => set({ focus }),
  select: (id, opts) =>
    set((s) => {
      // Selecting an asset always pivots focus to the asset pane — that's the
      // whole point of clicking the asset.
      if (opts?.multi) {
        // Multi-click toggles membership: shift-clicking a selected asset
        // removes it from the set.
        const next = new Set(s.selected);
        if (next.has(id)) {
          next.delete(id);
          log.select(`- ${id} (multi)`, { selected: Array.from(next) });
        } else {
          next.add(id);
          log.select(`+ ${id} (multi)`, { selected: Array.from(next) });
        }
        return { selected: next, focus: 'asset' };
      }
      // Clicking the sole selected asset again DESELECTS it (operator ask).
      if (s.selected.size === 1 && s.selected.has(id)) {
        log.select(`× ${id} (re-click deselect)`, { selected: [] });
        return { selected: new Set<string>(), focus: 'asset' };
      }
      log.select(`= ${id}`, { selected: [id] });
      return { selected: new Set([id]), focus: 'asset' };
    }),
  selectMany: (ids) =>
    set(() => {
      log.select(`= [${ids.join(', ')}]`, { selected: ids });
      return { selected: new Set(ids), focus: 'asset' };
    }),
  deselect: (id) =>
    set((s) => {
      const next = new Set(s.selected);
      next.delete(id);
      return { selected: next };
    }),
  clear: () => set({ selected: new Set() }),
  toggle: (id) =>
    set((s) => {
      const next = new Set(s.selected);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { selected: next };
    }),
  isSelected: (id) => get().selected.has(id),
  pruneTo: (validIds) =>
    set((s) => {
      const valid = new Set(validIds);
      let changed = false;
      const next = new Set<string>();
      for (const id of s.selected) {
        if (valid.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? { selected: next } : s;
    }),
}));
